import '../../pages/mobile/SdkPerformance.css';
import './ReleasePerformancePanel.css';
import { useEffect, useMemo, useState } from 'react';
import { Gauge, ArrowLeftRight, Info, RefreshCw } from 'lucide-react';
import WipPill from '../common/WipPill';
import useReleasePerformance from '../../hooks/useReleasePerformance';
import {
  KPIS, PLATFORM_LABELS, DEVICE_ALL, SDK_RUN_ALL, RELEASE_NOISE_FLOOR,
} from '../../pages/mobile/sdk-performance/constants';
import { helpText, Help } from '../../pages/mobile/sdk-performance/help';
import { displayDeviceLabel, formatDate } from '../../pages/mobile/sdk-performance/format';
import {
  usualRange, releaseVerdictOf, runMetrics, runCountOf, runOptions, deviceOptions,
  summarizeMetrics, figureDevices, sameDevices, sharedIosBaselines,
} from '../../pages/mobile/sdk-performance/metrics';
import { KpiCard, Takeaway, RunContext } from '../../pages/mobile/sdk-performance/KpiCards';
import { ReleaseTrend } from '../../pages/mobile/sdk-performance/ReleaseTrend';
import { SessionReplayStagesTable, ScenarioTable } from '../../pages/mobile/sdk-performance/ReleaseTables';

const MAX_EARLIER_RELEASES = 10;
const CSQ_MODE_LABELS = { legacy: 'Legacy', unified: 'Unified' };

// "[PERF] v4.53.1" -> "v4.53.1"
export function releaseLabel(run) {
  const runName = run?.release?.runName;
  if (runName) return runName.replace(/^\[PERF\]\s*/, '');
  return run?.release?.version ? `v${run.release.version}` : 'previous release';
}

// Every metric of the response, the run's own first, once per run.
function responseMetrics(data) {
  const comparison = data?.comparison;
  const sources = [
    data?.metrics,
    comparison?.previousReleaseMetrics,
    ...(comparison?.releaseTrend || []).map((entry) => entry.metrics),
  ];
  const byRun = new Map();
  sources.forEach((metrics) => {
    const groups = new Map();
    (metrics || []).forEach((metric) => groups.set(metric.runId, [...(groups.get(metric.runId) || []), metric]));
    groups.forEach((group, runId) => { if (!byRun.has(runId)) byRun.set(runId, group); });
  });
  return [...byRun.values()].flat();
}

function BaselineBar({ previousRelease, previousLabel, run, platform }) {
  if (previousRelease) {
    return (
      <div className="rpp-basis rpp-basis-release" data-basis="release">
        <ArrowLeftRight size={15} />
        <span>
          Baseline: <b>previous release {previousLabel}</b> (performance run of {formatDate(previousRelease.uploadedAt)}, same CSQ mode and platform).
        </span>
      </div>
    );
  }
  const flavor = [CSQ_MODE_LABELS[run?.release?.csqMode], PLATFORM_LABELS[platform]].filter(Boolean).join(' ');
  return (
    <div className="rpp-basis rpp-basis-none" data-basis="none">
      <Info size={15} />
      <span><b>No earlier {flavor} release performance run found</b>, so there is nothing to compare this release with.</span>
    </div>
  );
}

function VerdictPill({ rows }) {
  const judged = rows.filter((row) => row.verdict);
  if (!judged.length) return null;
  const above = judged.filter((row) => row.verdict === 'above').length;
  const below = judged.filter((row) => row.verdict === 'below').length;
  if (above) return <span className="rpp-pill rpp-pill-warn">{above} above baseline</span>;
  return <span className="rpp-pill rpp-pill-ok">{below ? `${below} below baseline` : 'All steady'}</span>;
}

function ReleasePerformancePanel({ runId, runName, cycleName }) {
  const [selectedDevice, setSelectedDevice] = useState(DEVICE_ALL);
  const [selectedRun, setSelectedRun] = useState(SDK_RUN_ALL);
  const [trendKey, setTrendKey] = useState('cpu');
  const allRepeats = useReleasePerformance(runId, SDK_RUN_ALL);
  const oneRepeat = useReleasePerformance(runId, selectedRun === SDK_RUN_ALL ? null : selectedRun);
  const response = selectedRun === SDK_RUN_ALL ? allRepeats : oneRepeat;
  const loading = allRepeats.loading || response.loading;
  const error = allRepeats.error || response.error;
  const refetch = () => {
    allRepeats.refetch();
    if (selectedRun !== SDK_RUN_ALL) oneRepeat.refetch();
  };

  const data = response.data;
  const run = data?.run || null;
  const comparison = data?.comparison;
  const previousRelease = comparison?.previousRelease || null;
  const previousLabel = releaseLabel(previousRelease);
  const platform = run?.platform || data?.metrics?.[0]?.platform || 'android';

  const metrics = useMemo(() => {
    const all = responseMetrics(data);
    const shared = selectedRun === SDK_RUN_ALL ? [] : sharedIosBaselines(all, responseMetrics(allRepeats.data));
    return [...all, ...shared].filter((metric) => metric.platform === platform);
  }, [data, allRepeats.data, selectedRun, platform]);
  const currentAll = useMemo(() => metrics.filter((metric) => metric.runId === run?.id), [metrics, run?.id]);
  const devices = useMemo(() => deviceOptions(currentAll, platform), [currentAll, platform]);
  const repeats = runOptions(runCountOf(allRepeats.data?.metrics));
  const kpis = useMemo(() => (KPIS[platform] || []).filter((kpi) => !kpi.optional || metrics.some(kpi.reports)), [metrics, platform]);
  const trendKpi = kpis.find((kpi) => kpi.key === trendKey) || kpis[0];

  const filtered = (id) => runMetrics(metrics, id, platform, selectedDevice);
  const current = run ? filtered(run.id) : [];
  const currentSummary = summarizeMetrics(current);
  const previous = previousRelease ? filtered(previousRelease.id) : null;
  const previousSummary = previous ? summarizeMetrics(previous) : null;
  const earlierReleases = (comparison?.releaseTrend?.length
    ? comparison.releaseTrend.map((entry) => entry.run)
    : [previousRelease].filter(Boolean))
    .sort((a, b) => new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime())
    .slice(-MAX_EARLIER_RELEASES)
    .map((release) => ({ run: release, summary: summarizeMetrics(filtered(release.id)) }));

  const rows = (trendKpi ? kpis : []).map((kpi) => {
    const value = kpi.read(currentSummary);
    if (!previousRelease) return { kpi, value, verdict: null };
    const comparable = sameDevices(kpi, previousSummary, currentSummary);
    const previousValue = comparable ? kpi.read(previousSummary) : null;
    return {
      kpi,
      value,
      previous: { value: previousValue, label: previousLabel, comparable },
      verdict: comparable ? releaseVerdictOf(value, previousValue, RELEASE_NOISE_FLOOR[kpi.key]) : null,
    };
  });
  const trendRow = rows.find((row) => row.kpi.key === trendKpi?.key);
  const trendRange = trendKpi && usualRange(earlierReleases
    .filter((entry) => sameDevices(trendKpi, entry.summary, currentSummary))
    .map((entry) => trendKpi.read(entry.summary)));
  // An earlier run measured on other devices is left out of the trend.
  const trendValue = (summary) => (sameDevices(trendKpi, summary, currentSummary) ? trendKpi.read(summary) : null);
  const trendPoints = trendKpi ? [
    ...earlierReleases.map((entry) => ({ label: releaseLabel(entry.run), value: trendValue(entry.summary) })),
    { label: 'This release', value: trendKpi.read(currentSummary) },
  ] : [];
  const replicaHint = selectedRun === SDK_RUN_ALL
    ? (repeats.length ? `Average of the ${repeats.length} runs.` : '')
    : `Run ${selectedRun} only.`;

  useEffect(() => {
    if (selectedDevice !== DEVICE_ALL && !devices.includes(selectedDevice)) setSelectedDevice(DEVICE_ALL);
  }, [devices, selectedDevice]);

  return (
    <div className="sdkp-page rpp-panel" data-panel="release-performance">
      <div className="rpp-crumb">{cycleName ? `${cycleName} › ` : ''}{run?.release?.runName || runName}</div>
      <div className="sdkp-header">
        <h2 className="sdkp-title rpp-title">
          <Gauge size={22} />
          {run?.release?.runName || runName}
          <WipPill />
          {!loading && <VerdictPill rows={rows} />}
        </h2>
        <div className="sdkp-header-actions">
          <select
            className="sdkp-select"
            aria-label="Device filter"
            value={selectedDevice}
            onChange={(event) => setSelectedDevice(event.target.value)}
            disabled={!devices.length}
          >
            <option value={DEVICE_ALL}>All devices</option>
            {devices.map((device) => <option key={device} value={device}>{displayDeviceLabel(device)}</option>)}
          </select>
          <span className="sdkp-filter-with-help">
            <select
              className="sdkp-select"
              aria-label="Run filter"
              value={selectedRun}
              onChange={(event) => setSelectedRun(event.target.value)}
              disabled={!repeats.length}
            >
              <option value={SDK_RUN_ALL}>{repeats.length ? `All ${repeats.length} runs (average)` : 'All runs'}</option>
              {repeats.map((repeat) => <option key={repeat} value={repeat}>Run {repeat}</option>)}
            </select>
            {repeats.length > 0 && <Help text={helpText('repeats', platform, { runCount: repeats.length })} placement="below" align="right" />}
          </span>
          <button type="button" className="sdkp-link-button" onClick={refetch} title="Refresh" disabled={loading}>
            <RefreshCw size={15} />
          </button>
        </div>
        <p className="sdkp-subtitle">
          Release performance test{cycleName ? ` for ${cycleName}` : ''}: how much extra CPU, memory and network the SDK adds to the test app, measured by the release workflow.
        </p>
      </div>

      {error && (
        <div className="sdkp-notice sdkp-error">
          {error.response?.status === 404 ? 'This release performance run was not found.' : 'Failed to load release performance data from Quality Hub.'}
        </div>
      )}
      {loading && <div className="sdkp-empty">Loading…</div>}

      {!loading && !error && run && (
        <>
          <RunContext run={run} summary={currentSummary} deviceCount={currentSummary.devices.length} label="Release run" />
          <BaselineBar previousRelease={previousRelease} previousLabel={previousLabel} run={run} platform={platform} />
          {!current.length && <div className="sdkp-notice">This run reported no performance figures for the selected filters.</div>}
          {previousRelease && <Takeaway rows={rows} platform={platform} previousLabel={previousLabel} />}
          <div className="sdkp-kpis">
            {rows.map(({ kpi, value, previous: previousFigure, verdict }) => {
              const kpiDevices = figureDevices(kpi, currentSummary);
              return (
                <KpiCard
                  key={kpi.key}
                  kpi={kpi}
                  platform={platform}
                  value={value}
                  previous={previousFigure}
                  noComparison={!previousRelease}
                  verdict={verdict}
                  figure={kpi.metric ? currentSummary.figures?.[kpi.metric] : null}
                  devices={kpiDevices}
                  onlyOfMany={selectedDevice === DEVICE_ALL && kpiDevices.length === 1 && currentSummary.devices.length > 1}
                  conclusion={run.conclusion}
                  active={kpi.key === trendKpi?.key}
                />
              );
            })}
          </div>
          {trendKpi && (
            <ReleaseTrend
              points={trendPoints}
              range={trendRange}
              kpis={kpis}
              kpi={trendKpi}
              onSelect={setTrendKey}
              verdict={trendRow?.verdict}
            />
          )}
          <SessionReplayStagesTable
            platform={platform}
            current={current}
            previous={previous}
            previousLabel={previousLabel}
          />
          <ScenarioTable
            platform={platform}
            current={current}
            previous={previous}
            previousLabel={previousLabel}
            hint={replicaHint}
          />
        </>
      )}
    </div>
  );
}

export default ReleasePerformancePanel;
